import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, CurrenciesUSD, Icon, Table, TableColumn, TableVariant, Tooltip } from '@column/column-ui-kit';
import { Badge } from '@column/ui';

import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { type BadgeTone, getTransferBadgeClassName } from '~/lib/transfers/utils';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatchRecord } from '~/typings/API';

import { TransferDetailsSheet } from './TransferDetailsSheet';
import { TRANSFER_STATUS_META, transferUrlFor } from './transferHelpers';

const StackedCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  padding: 4px 16px;
  height: 40px;
  width: 100%;
  box-sizing: border-box;
`;

const StackedPrimary = styled.span<{ $medium?: boolean }>`
  font-size: 14px;
  line-height: 18px;
  font-weight: ${({ $medium }) => ($medium ? 500 : 400)};
  color: ${({ theme }) => theme.foreground};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackedNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackedCell: React.FC<{ primary: React.ReactNode; note?: string | null; mediumPrimary?: boolean }> = ({
  primary,
  note,
  mediumPrimary,
}) => (
  <StackedCellWrapper>
    <StackedPrimary $medium={mediumPrimary}>{primary}</StackedPrimary>
    {note ? <StackedNote>{note}</StackedNote> : null}
  </StackedCellWrapper>
);

const POLL_MS = 5000;

const POLLING_BATCH_STATUSES = new Set(['validating', 'processing']);

const TYPE_LABEL: Record<string, string> = {
  credit: 'Credit',
  debit: 'Debit',
};

// Map a transfer status's chip color onto the badge tone, so the Status column keeps its semantic color
// while adopting the Type column's badge styling.
const STATUS_BADGE_TONE: Record<string, BadgeTone> = {
  default: 'secondary',
  info: 'info',
  success: 'success',
  danger: 'danger',
};

const columns: TableColumn<ACHTransferBatchRecord>[] = [
  {
    header: 'Amount / Description',
    accessor: 'amount',
    // Full width — takes the bulk of the flexible space. Amount:Receiver of 3:1 keeps Receiver to about
    // a quarter of that space (roughly half its previous, even 1fr:1fr, share).
    width: { min: 180, max: '3fr' },
    cell: ({ row, value }) => (
      <Table.AmountCell
        currencyList={CurrenciesUSD}
        fixedCurrencyCode={row.currency}
        value={value}
        note={row.description || undefined}
        isBold
        isColorized
      />
    ),
  },
  {
    header: 'Type',
    accessor: 'type',
    width: { fixed: 'min-content' },
    cell: ({ value }) => {
      // Match the transfers table's Direction column exactly (Badge + getTransferBadgeClassName): credit
      // is outgoing (up), debit is incoming (down); the class sizes the icon to 16px and the text to xs.
      const directionIcon =
        value === 'credit' ? <Icon.CircleArrowUp /> : value === 'debit' ? <Icon.CircleArrowDown /> : null;
      return (
        <Table.Cell>
          <Badge variant="ghost" className={getTransferBadgeClassName()}>
            {directionIcon}
            {TYPE_LABEL[value ?? ''] ?? value ?? '-'}
          </Badge>
        </Table.Cell>
      );
    },
  },
  {
    header: 'Receiver',
    accessor: 'receiverName',
    width: { min: 160, max: '1fr' },
    cell: ({ row, value }) => <StackedCell primary={value || '-'} note={row.companyName || null} mediumPrimary />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { fixed: 'min-content' },
    cell: ({ value }) => {
      const meta = TRANSFER_STATUS_META[value] ?? { type: 'default' as const, label: value };
      const tone = STATUS_BADGE_TONE[meta.type ?? 'default'] ?? 'secondary';
      return (
        <Table.Cell>
          {/* Same badge styling as the Type column; a check accompanies the "valid" status. */}
          <Badge variant="ghost" className={getTransferBadgeClassName(tone)}>
            {value === 'valid' ? <Icon.CircleCheck /> : null}
            {meta.label}
          </Badge>
        </Table.Cell>
      );
    },
  },
  {
    header: 'Error',
    accessor: 'error',
    width: { fixed: 'min-content' },
    cell: ({ row }) =>
      row.error ? (
        <Tooltip content={row.error.message} placement="top">
          <StackedCell primary={row.error.message} note={row.error.code} />
        </Tooltip>
      ) : (
        <Table.Cell>-</Table.Cell>
      ),
  },
];

const STATUS_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  ...Object.entries(TRANSFER_STATUS_META).map(([value, { label }]) => ({ label, value })),
];

// Bucket so transitions within a phase don't bust the transfers query key.
type TransfersPhase = 'in-flight' | 'post-validation' | 'post-creation';
const TRANSFERS_PHASE: Record<string, TransfersPhase> = {
  validated: 'post-validation',
  canceled: 'post-validation',
  expired: 'post-validation',
  completed: 'post-creation',
  failed: 'post-creation',
};

interface AchBatchTransfersTableProps {
  batchId: string;
  batchStatus: string;
  statusFilter: string;
  /** Fired once the first records fetch resolves, so the caller can defer animating the table in until it has data. */
  onReady?: () => void;
}

export const AchBatchTransfersTable: React.FC<AchBatchTransfersTableProps> = ({
  batchId,
  batchStatus,
  statusFilter,
  onReady,
}) => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const navigate = useNavigate();
  // 50 per page: with ≤50 transfers everything fits on one page and the pagination controls stay hidden.
  const { cursor, onNext, setPageSize, reset, ...pagination } = usePaginationState({ defaultPageSize: 50 });

  const [prevFilter, setPrevFilter] = useState(statusFilter);
  let effectiveCursor: string | undefined = cursor;
  if (prevFilter !== statusFilter) {
    setPrevFilter(statusFilter);
    reset();
    effectiveCursor = undefined;
  }

  const { data, isLoading, isPlaceholderData, isError } = useQuery({
    queryKey: [
      'ach-batch-records',
      platformId,
      isSandbox,
      batchId,
      effectiveCursor,
      pagination.pageSize,
      statusFilter,
      TRANSFERS_PHASE[batchStatus] ?? 'in-flight',
    ],
    queryFn: () =>
      AchBatchRepository.getRecords(batchId, {
        limit: pagination.pageSize,
        startingAfter: effectiveCursor,
        status: statusFilter || undefined,
      }),
    enabled: !!batchId,
    refetchInterval: () => (POLLING_BATCH_STATUSES.has(batchStatus) ? POLL_MS : false),
    // A batchStatus phase change (see TRANSFERS_PHASE) starts a fresh query; keep the prior transfers
    // rendered through it so the table doesn't collapse to its loading state and remount.
    placeholderData: keepPreviousData,
  });

  const transfers = data?.records ?? [];

  // The Error column only earns its space when something actually failed, so hide it until a record on
  // this page carries an error.
  const visibleColumns = React.useMemo(
    () => (transfers.some((t) => t.error) ? columns : columns.filter((column) => column.accessor !== 'error')),
    [transfers]
  );

  // Tell the caller once real records for the current batch have loaded so it can hold the reveal
  // animation until the rows are ready. Excludes keepPreviousData's placeholder (which would otherwise
  // read as "ready" while the new batch is still fetching). Kept in a ref so a changing onReady
  // identity doesn't retrigger it. Also fire on a records-fetch error so the section still reveals (with
  // the empty/error state and the reset affordance) instead of dead-ending on an empty header.
  const hasData = data !== undefined && !isPlaceholderData;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    if (hasData || isError) {
      onReadyRef.current?.();
    }
  }, [hasData, isError]);

  // Force remount once data arrives so ui-kit measures the action column with body cells.
  const tableMountKey = data === undefined ? 'pre' : 'ready';

  const rowAction = useCallback(
    (transfer: ACHTransferBatchRecord) => {
      const url = transferUrlFor(transfer);
      return url ? (
        <Button
          onClick={(event: React.SyntheticEvent) => {
            event.stopPropagation();
            navigate(url);
          }}
          icon={<Icon.ChevronRightLarge />}
          iconRight
          size="tiny"
          variant="secondary"
        >
          View
        </Button>
      ) : null;
    },
    [navigate]
  );

  const [openTransferId, setOpenTransferId] = useState<string | null>(null);
  const openTransfer = openTransferId ? (transfers.find((t) => t.id === openTransferId) ?? null) : null;

  // Only show pagination once the transfers spill past one page (>50) — either there's a next page or
  // we've already navigated forward.
  const showPagination = (data?.hasMore ?? false) || !!effectiveCursor;

  return (
    <>
      <Table<ACHTransferBatchRecord>
        key={tableMountKey}
        columns={visibleColumns}
        data={transfers}
        pagination={
          showPagination
            ? {
                hasNext: data?.hasMore ?? false,
                onNext: () => (transfers.length ? onNext({ cursor: transfers[transfers.length - 1].id }) : undefined),
                onPageSizeChange: setPageSize,
                ...pagination,
              }
            : undefined
        }
        isLoading={isLoading}
        rowAction={rowAction}
        variant={TableVariant.List}
        onRowClick={(row) => setOpenTransferId(row.id)}
        emptyMessage={
          isError ? "Couldn't load this batch's transfers. Reset and re-upload the file to try again." : undefined
        }
      />
      <TransferDetailsSheet
        transfer={openTransfer}
        open={!!openTransferId}
        onOpenChange={(open) => !open && setOpenTransferId(null)}
      />
    </>
  );
};

export { STATUS_FILTER_OPTIONS };
